/**
 * Playfield generator.
 *
 * Strategy (matches the open-arena feel of the original Splat!):
 *  1. Empty ground grid.
 *  2. Scatter small foliage clusters (1-3 tiles each, grown by random walk).
 *  3. Connectivity repair: flood-fill from spawn; if any ground is unreachable,
 *     punch out adjacent foliage until everything connects.
 *  4. Scatter water hazards (1-3 tile clusters) on ground tiles.
 *  5. Scatter spider patrols (2-4 tile linear segments) on clean ground.
 *  6. Scatter ~20 coconuts on remaining ground, ~15% marked invisible.
 *  7. Compute reachability (which coconuts can be reached without crossing hazards)
 *     so the debug view can flag any dead-end coconuts.
 *
 * All randomness flows from a single seeded RNG, so the same seed produces
 * identical output across machines and sessions.
 */

import { PLAYFIELD_TILES_W, PLAYFIELD_TILES_H } from '../config';
import { makeRng, type RNG } from './prng';
import {
  Tile,
  tileIdx,
  type Playfield,
  type Coconut,
  type Spider,
  type Position,
} from './types';

// --- Tuning knobs ----------------------------------------------------------
// All of these are easy to tweak once we eyeball generator output.

const FOLIAGE_CLUSTERS_MIN = 18;
const FOLIAGE_CLUSTERS_MAX = 26;
const FOLIAGE_CLUSTER_SIZE_MIN = 1;
const FOLIAGE_CLUSTER_SIZE_MAX = 3;
/** Min Chebyshev distance between cluster seed points. */
const FOLIAGE_CLUSTER_SPACING = 2;

const WATER_CLUSTERS_MIN = 3;
const WATER_CLUSTERS_MAX = 5;
const WATER_CLUSTER_SIZE_MIN = 1;
const WATER_CLUSTER_SIZE_MAX = 3;

const SPIDER_COUNT_MIN = 3;
const SPIDER_COUNT_MAX = 5;
const SPIDER_SEGMENT_MIN = 2;
const SPIDER_SEGMENT_MAX = 4;

const COCONUT_COUNT = 20;
const COCONUT_INVISIBLE_CHANCE = 0.15;
const COCONUT_MIN_SPACING = 2; // Manhattan distance between coconuts

/** Half-width of the protected 3x3 zone around spawn. */
const SPAWN_PROTECT_RADIUS = 1;

/** Min Manhattan distance from spawn for hazards / spiders. Keeps spawn breathing room. */
const SPAWN_HAZARD_MARGIN = 4;

// --------------------------------------------------------------------------

export interface GeneratorOptions {
  /** Multiplier on water cluster count (Adventure mode scaling). Default 1. */
  waterMultiplier?: number;
  /** Multiplier on spider count (reserved for future scaling). Default 1. */
  spiderMultiplier?: number;
}

export function generatePlayfield(
  seed: string,
  opts: GeneratorOptions = {}
): Playfield {
  const t0 = performance.now();
  const rng = makeRng(seed);
  const W = PLAYFIELD_TILES_W;
  const H = PLAYFIELD_TILES_H;
  const tiles: Tile[] = new Array(W * H).fill(Tile.Ground);

  const waterMul = opts.waterMultiplier ?? 1;
  const spiderMul = opts.spiderMultiplier ?? 1;

  const idx = (x: number, y: number) => tileIdx(W, x, y);
  const inBounds = (x: number, y: number) => x >= 0 && x < W && y >= 0 && y < H;

  // Spawn at the playfield center.
  const spawn: Position = { x: Math.floor(W / 2), y: Math.floor(H / 2) };
  const isSpawnProtected = (x: number, y: number) =>
    Math.abs(x - spawn.x) <= SPAWN_PROTECT_RADIUS &&
    Math.abs(y - spawn.y) <= SPAWN_PROTECT_RADIUS;
  const distToSpawn = (x: number, y: number) =>
    Math.abs(x - spawn.x) + Math.abs(y - spawn.y);

  // --- Foliage clusters --------------------------------------------------
  placeFoliageClusters(rng, tiles, W, H, isSpawnProtected, inBounds);

  // --- Connectivity repair -----------------------------------------------
  ensureConnectivity(rng, tiles, W, H, spawn);

  // --- Water hazards -----------------------------------------------------
  placeWater(rng, tiles, W, H, isSpawnProtected, distToSpawn, inBounds, waterMul);

  // --- Spiders -----------------------------------------------------------
  const occupied = new Set<number>();
  const spiders = placeSpiders(
    rng,
    tiles,
    W,
    H,
    occupied,
    isSpawnProtected,
    distToSpawn,
    idx,
    spiderMul
  );

  // --- Coconuts ----------------------------------------------------------
  const coconuts = placeCoconuts(
    rng,
    tiles,
    W,
    H,
    occupied,
    isSpawnProtected,
    idx
  );

  // --- Reachability analysis --------------------------------------------
  const hazardSet = computeHazardSet(tiles, spiders, idx);
  const reachable = bfsReachable(tiles, W, H, spawn, hazardSet);

  for (const c of coconuts) {
    c.reachable = reachable.has(idx(c.x, c.y));
  }

  // --- Stats ------------------------------------------------------------
  let foliageTiles = 0;
  let waterTiles = 0;
  let totalGroundTiles = 0;
  for (const t of tiles) {
    if (t === Tile.Foliage) foliageTiles++;
    else if (t === Tile.Water) waterTiles++;
    else totalGroundTiles++;
  }

  const spiderTiles = spiders.reduce((acc, sp) => acc + sp.length, 0);

  // Pure walkability (ignoring hazards): reachable ground tiles only.
  const walkable = bfsReachable(tiles, W, H, spawn, new Set());
  let connectedGroundTiles = 0;
  for (const i of walkable) {
    if (tiles[i] === Tile.Ground) connectedGroundTiles++;
  }

  const coconutsReachable = coconuts.filter((c) => c.reachable).length;
  const generationMs = Math.round(performance.now() - t0);

  return {
    width: W,
    height: H,
    tiles,
    coconuts,
    spiders,
    spawn,
    seed,
    stats: {
      foliageTiles,
      waterTiles,
      spiderTiles,
      totalGroundTiles,
      connectedGroundTiles,
      coconutsReachable,
      generationMs,
    },
  };
}

// --- Stages ----------------------------------------------------------------

function placeFoliageClusters(
  rng: RNG,
  tiles: Tile[],
  W: number,
  H: number,
  isSpawnProtected: (x: number, y: number) => boolean,
  inBounds: (x: number, y: number) => boolean
): void {
  const target = rng.int(FOLIAGE_CLUSTERS_MIN, FOLIAGE_CLUSTERS_MAX);
  const seeds: Position[] = [];

  // Place cluster seed points with min Chebyshev spacing.
  let attempts = 0;
  while (seeds.length < target && attempts < 500) {
    attempts++;
    const x = rng.int(0, W - 1);
    const y = rng.int(0, H - 1);
    if (isSpawnProtected(x, y)) continue;
    let tooClose = false;
    for (const s of seeds) {
      if (
        Math.max(Math.abs(s.x - x), Math.abs(s.y - y)) <
        FOLIAGE_CLUSTER_SPACING
      ) {
        tooClose = true;
        break;
      }
    }
    if (tooClose) continue;
    seeds.push({ x, y });
  }

  // Grow each seed into a small cluster via random walk.
  for (const s of seeds) {
    const size = rng.int(FOLIAGE_CLUSTER_SIZE_MIN, FOLIAGE_CLUSTER_SIZE_MAX);
    let cx = s.x;
    let cy = s.y;
    for (let i = 0; i < size; i++) {
      if (inBounds(cx, cy) && !isSpawnProtected(cx, cy)) {
        tiles[cy * W + cx] = Tile.Foliage;
      }
      const dir = rng.int(0, 3);
      if (dir === 0) cx++;
      else if (dir === 1) cx--;
      else if (dir === 2) cy++;
      else cy--;
    }
  }
}

function ensureConnectivity(
  rng: RNG,
  tiles: Tile[],
  W: number,
  H: number,
  spawn: Position
): void {
  // Repeatedly flood-fill; if any non-foliage tile is unreachable, punch a
  // hole in the foliage adjacent to it. Cap iterations to prevent any chance
  // of an infinite loop.
  for (let iter = 0; iter < 100; iter++) {
    const reachable = bfsReachable(tiles, W, H, spawn, new Set());

    const unreachable: number[] = [];
    for (let i = 0; i < tiles.length; i++) {
      if (tiles[i] !== Tile.Foliage && !reachable.has(i)) {
        unreachable.push(i);
      }
    }
    if (unreachable.length === 0) return;

    // Pick a random unreachable tile and break through one of its
    // foliage neighbors.
    const target = unreachable[rng.int(0, unreachable.length - 1)]!;
    const tx = target % W;
    const ty = Math.floor(target / W);

    const foliageNeighbors: Position[] = [];
    const candidates: Position[] = [
      { x: tx - 1, y: ty },
      { x: tx + 1, y: ty },
      { x: tx, y: ty - 1 },
      { x: tx, y: ty + 1 },
    ];
    for (const n of candidates) {
      if (n.x < 0 || n.x >= W || n.y < 0 || n.y >= H) continue;
      if (tiles[n.y * W + n.x] === Tile.Foliage) foliageNeighbors.push(n);
    }

    if (foliageNeighbors.length === 0) {
      // Surrounded by edges only — shouldn't happen given our placement
      // rules, but guard against it.
      return;
    }

    const removeAt = foliageNeighbors[rng.int(0, foliageNeighbors.length - 1)]!;
    tiles[removeAt.y * W + removeAt.x] = Tile.Ground;
  }
}

function placeWater(
  rng: RNG,
  tiles: Tile[],
  W: number,
  H: number,
  isSpawnProtected: (x: number, y: number) => boolean,
  distToSpawn: (x: number, y: number) => number,
  inBounds: (x: number, y: number) => boolean,
  multiplier: number
): void {
  const baseMin = Math.round(WATER_CLUSTERS_MIN * multiplier);
  const baseMax = Math.round(WATER_CLUSTERS_MAX * multiplier);
  const target = rng.int(Math.max(0, baseMin), Math.max(baseMin, baseMax));
  for (let i = 0; i < target; i++) {
    // Try a few times to find a spot on ground, with breathing room from spawn.
    let placed = false;
    for (let attempt = 0; attempt < 60 && !placed; attempt++) {
      const x = rng.int(0, W - 1);
      const y = rng.int(0, H - 1);
      if (tiles[y * W + x] !== Tile.Ground) continue;
      if (isSpawnProtected(x, y)) continue;
      if (distToSpawn(x, y) < SPAWN_HAZARD_MARGIN) continue;

      const size = rng.int(WATER_CLUSTER_SIZE_MIN, WATER_CLUSTER_SIZE_MAX);
      let cx = x;
      let cy = y;
      for (let s = 0; s < size; s++) {
        if (
          inBounds(cx, cy) &&
          tiles[cy * W + cx] === Tile.Ground &&
          !isSpawnProtected(cx, cy)
        ) {
          tiles[cy * W + cx] = Tile.Water;
        }
        const dir = rng.int(0, 3);
        if (dir === 0) cx++;
        else if (dir === 1) cx--;
        else if (dir === 2) cy++;
        else cy--;
      }
      placed = true;
    }
  }
}

function placeSpiders(
  rng: RNG,
  tiles: Tile[],
  W: number,
  H: number,
  occupied: Set<number>,
  isSpawnProtected: (x: number, y: number) => boolean,
  distToSpawn: (x: number, y: number) => number,
  idx: (x: number, y: number) => number,
  multiplier: number
): Spider[] {
  const spiders: Spider[] = [];
  const baseMin = Math.round(SPIDER_COUNT_MIN * multiplier);
  const baseMax = Math.round(SPIDER_COUNT_MAX * multiplier);
  const target = rng.int(Math.max(0, baseMin), Math.max(baseMin, baseMax));

  for (let i = 0; i < target; i++) {
    for (let attempt = 0; attempt < 60; attempt++) {
      const length = rng.int(SPIDER_SEGMENT_MIN, SPIDER_SEGMENT_MAX);
      const axis: 'horizontal' | 'vertical' =
        rng.int(0, 1) === 0 ? 'horizontal' : 'vertical';
      const x = rng.int(1, W - (axis === 'horizontal' ? length + 1 : 2));
      const y = rng.int(1, H - (axis === 'vertical' ? length + 1 : 2));

      // Validate every tile in the segment is plain ground, untaken, off-spawn.
      let valid = true;
      for (let k = 0; k < length; k++) {
        const sx = axis === 'horizontal' ? x + k : x;
        const sy = axis === 'vertical' ? y + k : y;
        if (tiles[idx(sx, sy)] !== Tile.Ground) {
          valid = false;
          break;
        }
        if (occupied.has(idx(sx, sy))) {
          valid = false;
          break;
        }
        if (isSpawnProtected(sx, sy)) {
          valid = false;
          break;
        }
        if (distToSpawn(sx, sy) < SPAWN_HAZARD_MARGIN) {
          valid = false;
          break;
        }
      }
      if (!valid) continue;

      // Commit.
      for (let k = 0; k < length; k++) {
        const sx = axis === 'horizontal' ? x + k : x;
        const sy = axis === 'vertical' ? y + k : y;
        occupied.add(idx(sx, sy));
      }
      spiders.push({
        axis,
        start: { x, y },
        length,
        initialOffset: rng.int(0, length - 1),
      });
      break;
    }
  }

  return spiders;
}

function placeCoconuts(
  rng: RNG,
  tiles: Tile[],
  W: number,
  H: number,
  occupied: Set<number>,
  isSpawnProtected: (x: number, y: number) => boolean,
  idx: (x: number, y: number) => number
): Coconut[] {
  const coconuts: Coconut[] = [];
  for (let i = 0; i < COCONUT_COUNT; i++) {
    for (let attempt = 0; attempt < 120; attempt++) {
      const x = rng.int(0, W - 1);
      const y = rng.int(0, H - 1);
      if (tiles[idx(x, y)] !== Tile.Ground) continue;
      if (isSpawnProtected(x, y)) continue;
      if (occupied.has(idx(x, y))) continue;

      let tooClose = false;
      for (const c of coconuts) {
        if (Math.abs(c.x - x) + Math.abs(c.y - y) < COCONUT_MIN_SPACING) {
          tooClose = true;
          break;
        }
      }
      if (tooClose) continue;

      coconuts.push({
        x,
        y,
        invisible: rng.chance(COCONUT_INVISIBLE_CHANCE),
        reachable: false, // filled in by reachability pass
      });
      occupied.add(idx(x, y));
      break;
    }
  }
  return coconuts;
}

// --- Utilities -------------------------------------------------------------

function computeHazardSet(
  tiles: Tile[],
  spiders: Spider[],
  idx: (x: number, y: number) => number
): Set<number> {
  const set = new Set<number>();
  for (let i = 0; i < tiles.length; i++) {
    if (tiles[i] === Tile.Water) set.add(i);
  }
  for (const sp of spiders) {
    for (let k = 0; k < sp.length; k++) {
      const sx = sp.axis === 'horizontal' ? sp.start.x + k : sp.start.x;
      const sy = sp.axis === 'vertical' ? sp.start.y + k : sp.start.y;
      set.add(idx(sx, sy));
    }
  }
  return set;
}

/**
 * BFS from spawn through tiles that are Ground (not Foliage, not Water) and
 * not in the hazard set. Returns the set of reachable tile indices.
 *
 * - Pass empty hazardSet for pure walkability (ignores hazards entirely).
 * - Pass populated hazardSet to see what's reachable *avoiding* danger.
 */
function bfsReachable(
  tiles: Tile[],
  W: number,
  H: number,
  start: Position,
  hazardSet: Set<number>
): Set<number> {
  const visited = new Set<number>();
  const queue: number[] = [start.y * W + start.x];

  while (queue.length > 0) {
    const i = queue.pop()!;
    if (visited.has(i)) continue;
    if (tiles[i] === Tile.Foliage) continue;
    if (tiles[i] === Tile.Water) continue;
    if (hazardSet.has(i)) continue;

    visited.add(i);

    const x = i % W;
    const y = Math.floor(i / W);
    if (x > 0) queue.push(i - 1);
    if (x < W - 1) queue.push(i + 1);
    if (y > 0) queue.push(i - W);
    if (y < H - 1) queue.push(i + W);
  }

  return visited;
}
